import { useEffect, useState } from 'react';
import { can } from '../../utils/permissions';
import { dossierLink } from '../../utils/dossierLink';
import { startIsolateBatch } from '../../utils/spores/sporeBatch';
import { availableModes } from '../workbench/modeList';
import { checkablePlate } from './quickActions';

/*
 * An isolate card's Lab analytics tray (docs/UI_HUBS_AND_GESTURES_PLAN.md
 * section 2, "Hub 4"): the analysis tools, opened on this isolate. Each
 * button is there only for whoever its tool lets in:
 *
 *   Choose an image...  first, given `onPicker`: the image source picker
 *                       (./ImageSourcePicker.js) that the card's swipe opens
 *                       too, here the way to it without a swipe (WCAG 2.5.1)
 *                       and the only one on a wide screen. For whoever may
 *                       open a workbench mode (pickerOffered).
 *   Thermal             lab:read. Its thermal captures, in a sheet over Home
 *                       (?tool=thermal&for=<id>, IsolateHub): Back closes it.
 *                       Importing still needs lab:observe (ThermalStrip).
 *   Culture / Petri     lab:read, and the isolate has plates. The analysis
 *                       workbench's culture mode with this isolate chosen in
 *                       its Isolate source, and its plate when one is the
 *                       obvious one (/tools/analysis?mode=culture&isolate=
 *                       &plate=&src=isolate).
 *   Spore / Microscopy  game:play: its micrographs handed to the analysis
 *                       workbench's microscopy mode as a batch
 *                       (utils/spores/sporeBatch.js), then the mode with this
 *                       isolate chosen (/tools/analysis?mode=microscopy
 *                       &isolate=). A lab reader who may not use that mode
 *                       (the company executive) gets the dossier's
 *                       Microscopy tab instead.
 *
 * IsolatesTable shows it under the row's Actions on a wide screen, and at the
 * card's physical left under 768 px, where the card slides aside to show it
 * when its toggle opens it (index.css .hub-tray; a finger's left-to-right
 * swipe opens the image source picker instead, hooks/useCardReveal.js, and
 * one right to left still closes the tray). `hidden` while closed;
 * Escape inside it closes it, and the table hands focus back to its toggle.
 * Open, it is the card's revealed element (data-card-reveal, which
 * useCardReveal measures). Paths into the tools carry state.returnTo: the
 * Home address it came from.
 */
export const TRAY_TOOLS = Object.freeze(['thermal', 'culture', 'spore']);

const plateCount = (item) => item?.counts?.plates ?? (item?.plates || []).length;

/** The tools a reader gets on this isolate's tray, in order; none, no tray. */
export function trayTools(item, user) {
  if (!item?.specimen_id) return [];
  const lab = can(user, 'lab:read');
  return TRAY_TOOLS.filter((tool) => {
    if (tool === 'thermal') return lab;
    if (tool === 'culture') return lab && plateCount(item) > 0;
    return can(user, 'game:play') || lab;
  });
}

/** Whether a reader is offered the image source picker on this isolate: one there is, and a workbench mode they may open. */
export const pickerOffered = (item, user) => !!item?.specimen_id && availableModes(user).length > 0;

/** The plate the dish station opens on: the row's default plate, else its first live one; none when no plate is live. */
export function obviousPlate(item) {
  const plates = item?.plates || [];
  const named = item?.default_plate_id ? plates.find((p) => p.plate_id === item.default_plate_id) : null;
  if (checkablePlate(named)) return named.plate_id;
  return plates.find(checkablePlate)?.plate_id || '';
}

/** The analysis workbench's culture mode (the dish scan station) on this isolate. */
export function culturePath(item) {
  const query = new URLSearchParams({ mode: 'culture', isolate: item.specimen_id });
  const plate = obviousPlate(item);
  if (plate) query.set('plate', plate);
  query.set('src', 'isolate');
  return `/tools/analysis?${query}`;
}

/** The analysis workbench's microscopy mode (spore analysis) on this isolate. */
export const sporePath = (item) => `/tools/analysis?mode=microscopy&isolate=${encodeURIComponent(item.specimen_id)}`;

/** Where the reader is now: the tools' way back to it. */
export function here() {
  try {
    return `${window.location.pathname}${window.location.search}`;
  } catch {
    return '/';
  }
}

export default function LabAnalyticsTray({
  item, user, t, open = false, dossiers, onThermal, onNavigate, onDossierTab = null, onEscape, onPicker = null,
}) {
  const id = item.specimen_id;
  const tools = trayTools(item, user);
  const picker = !!onPicker && pickerOffered(item, user);
  const workbench = can(user, 'game:play');
  // The spore button waits for the dossier it hands over (asked for when the tray opened).
  const [waiting, setWaiting] = useState(false);
  const entry = dossiers?.get?.(id);

  useEffect(() => {
    if (open && workbench && tools.includes('spore')) dossiers?.ensure?.(id);
  }, [open, workbench, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toWorkbench = (dossier) => {
    startIsolateBatch(item, dossier || null);
    onNavigate(sporePath(item), { returnTo: here() });
  };

  useEffect(() => {
    if (!waiting || !entry || entry.loading) return;
    if (!entry.data && !entry.error) return;
    setWaiting(false);
    toWorkbench(entry.data);
  }, [waiting, entry]); // eslint-disable-line react-hooks/exhaustive-deps

  const press = (tool) => {
    if (tool === 'thermal') {
      onThermal?.(item);
    } else if (tool === 'culture') {
      onNavigate(culturePath(item), { returnTo: here() });
    } else if (!workbench) {
      if (onDossierTab) onDossierTab(item, 'microscopy');
      else onNavigate(dossierLink(id, 'microscopy'));
    } else if (entry?.data || (entry?.error && !entry.loading)) {
      toWorkbench(entry.data);
    } else {
      setWaiting(true);
      dossiers?.ensure?.(id);
    }
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    onEscape?.(item);
  };

  if (!tools.length) return null;
  return (
    <div role="group" aria-label={t('tray.label', { code: item.specimen_code || '' })} id={`hub-tray-${id}`}
      className="hub-tray" hidden={!open} data-card-tray="" data-card-reveal={open ? '' : undefined}
      onKeyDown={onKeyDown} data-testid={`hub-tray-${id}`}>
      <div className="hub-tray__buttons">
        {picker && (
          <button type="button" className="hub-tray__button hub-tray__button--picker" aria-haspopup="dialog"
            onClick={(e) => onPicker(item, e.currentTarget)} title={t('tip.tray.picker')}
            data-testid={`hub-tray-picker-${id}`}>
            <span aria-hidden="true">🖼️ </span>{t('tray.picker')}
          </button>
        )}
        {tools.map((tool) => {
          const busy = tool === 'spore' && waiting;
          return (
            <button key={tool} type="button" className="hub-tray__button" onClick={() => press(tool)}
              aria-busy={busy || undefined} title={t(`tip.tray.${tool === 'spore' && !workbench ? 'sporeDossier' : tool}`)}
              data-testid={`hub-tray-${tool}-${id}`}>
              {t(`tray.${tool}`)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
